import { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { ConfigContext } from '../shared/ConfigContext';

const EMPTY_FORM = { companyName: '', companyPhone: '', companyEmail: '', companyAddress: '' };

const useMyCompanyPage = () => {
  const { t } = useTranslation();
  const config = useContext(ConfigContext);
  const companyId = localStorage.getItem('companyId') || '';

  const [status, setStatus] = useState('loading');
  const [errorInfo, setErrorInfo] = useState(null);
  const [companyLogo, setCompanyLogo] = useState('');

  const [form, setForm] = useState(EMPTY_FORM);
  const initialFormRef = useRef(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [logoLoading, setLogoLoading] = useState(false);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const notify = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  const fetchCompany = useCallback(() => {
    if (!config?.backendUrl || !companyId) { setStatus('ready'); return; }
    setStatus('loading');
    axios.get(`${config.backendUrl}/companies/${companyId}`)
      .then((res) => {
        const loaded = {
          companyName: res.data.companyName || '',
          companyPhone: res.data.companyPhone || '',
          companyEmail: res.data.companyEmail || '',
          companyAddress: res.data.companyAddress || '',
        };
        setForm(loaded);
        initialFormRef.current = loaded;
        setCompanyLogo(res.data.companyLogo || '');
        setStatus('ready');
      })
      .catch((error) => {
        console.error('Error fetching company data', error);
        if (error.response?.status === 403) {
          setStatus('permission');
        } else {
          setErrorInfo({
            request: `GET /companies/${companyId} · ${error.response?.status || t('N/A')} · ${new Date().toLocaleTimeString()}`,
          });
          setStatus('error');
        }
      });
  }, [config, companyId, t]);

  useEffect(() => { fetchCompany(); }, [fetchCompany]);

  const isDirty = JSON.stringify(form) !== JSON.stringify(initialFormRef.current);

  const discardChanges = () => setForm(initialFormRef.current);

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    try {
      await axios.put(`${config.backendUrl}/companies/${companyId}`, form);
      initialFormRef.current = form;
      notify(t('Company information updated successfully'));
    } catch (error) {
      console.error('Error updating company info', error);
      notify(t('Failed to update company information'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setSelectedFile(file);
    const reader = new FileReader();
    reader.onload = () => setPreviewUrl(reader.result);
    reader.readAsDataURL(file);
  };

  const handleLogoUpload = async () => {
    if (!selectedFile) return;
    setLogoLoading(true);
    const formData = new FormData();
    formData.append('companyLogo', selectedFile);
    try {
      await axios.post(`${config.backendUrl}/companies/${companyId}/logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      fetchCompany();
      notify(t('Company logo updated successfully'));
    } catch (error) {
      console.error('Error uploading logo', error);
      notify(t('Failed to update company logo'), 'error');
    } finally {
      setLogoLoading(false);
      setSelectedFile(null);
      setPreviewUrl('');
    }
  };

  return {
    t, status, errorInfo, fetchCompany, companyLogo,
    form, setForm, saving, isDirty, discardChanges, handleSave,
    selectedFile, previewUrl, logoLoading, handleFileChange, handleLogoUpload,
    snackbar, setSnackbar,
  };
};

export default useMyCompanyPage;
